// Keeps the reading position when switching directly between View and Edit of a note.
//
// The two pages lay the note out differently (Edit has a sticky editor toolbar, different padding and
// so different line wrapping), so a raw scrollTop would land somewhere else. Instead the position is
// anchored on the note content itself: which top-level block of the rendered note (paragraph,
// heading, table, collapsible section…) is at the top of the visible area, and how far into it.
// The other page scrolls that same block to the top at the same relative position.

/** Navigation-state key for the anchor (next to NOTE_TAB_STATE_KEY). */
export const NOTE_SCROLL_STATE_KEY = 'noteScroll';

export interface NoteScrollAnchor {
  /** Index of the top-level content block at the top of the visible area. */
  block: number;
  /** How far down that block the visible area starts (0 = its top, 1 = its bottom). */
  ratio: number;
  /** The view was scrolled to the very end: restore to the very end. */
  atEnd?: boolean;
}

/** The note content (rich editor) inside the scroll area's visible tab panel, if any. */
function visibleContent(scroller: HTMLElement): HTMLElement | null {
  const editors = Array.from(scroller.querySelectorAll<HTMLElement>('.ProseMirror'));
  return editors.find((el) => el.offsetParent !== null && !el.closest('[hidden]')) ?? null;
}

/** Top of the visible content area: the scroller's top, or below the editor toolbar when it's stuck there. */
function visibleTop(scroller: HTMLElement): number {
  const top = scroller.getBoundingClientRect().top;
  const toolbar = scroller.querySelector<HTMLElement>('.editor-toolbar-sticky');
  if (toolbar && toolbar.offsetParent !== null) {
    const rect = toolbar.getBoundingClientRect();
    if (rect.top <= top + 1 && rect.bottom > top) {
      return rect.bottom;
    }
  }
  return top;
}

export function captureNoteScrollAnchor(scroller: HTMLElement | null | undefined): NoteScrollAnchor | null {
  if (!scroller || scroller.scrollTop <= 0) {
    return null; // at the top: nothing to restore
  }
  const content = visibleContent(scroller);
  if (!content) return null;

  const atEnd = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
  const top = visibleTop(scroller);
  const blocks = Array.from(content.children) as HTMLElement[];
  for (let i = 0; i < blocks.length; i++) {
    const rect = blocks[i].getBoundingClientRect();
    if (rect.bottom > top) {
      const ratio = rect.height > 0 ? Math.min(1, Math.max(0, (top - rect.top) / rect.height)) : 0;
      return { block: i, ratio, ...(atEnd ? { atEnd: true } : {}) };
    }
  }
  return atEnd ? { block: blocks.length - 1, ratio: 1, atEnd: true } : null;
}

/** Scrolls so the anchored block is back at the top of the visible area. False if the content isn't there yet. */
export function applyNoteScrollAnchor(scroller: HTMLElement, anchor: NoteScrollAnchor): boolean {
  const content = visibleContent(scroller);
  const block = content?.children[anchor.block] as HTMLElement | undefined;
  if (!content || !block) return false;

  if (anchor.atEnd) {
    scroller.scrollTop = scroller.scrollHeight;
    return true;
  }
  const rect = block.getBoundingClientRect();
  const target = rect.top + rect.height * anchor.ratio;
  scroller.scrollTop += target - visibleTop(scroller);
  return true;
}

export function readNoteScrollAnchor(navigationState: unknown): NoteScrollAnchor | null {
  const value = (navigationState as Record<string, unknown> | null | undefined)?.[NOTE_SCROLL_STATE_KEY] as
    | Partial<NoteScrollAnchor>
    | undefined;
  if (!value || typeof value.block !== 'number' || value.block < 0 || typeof value.ratio !== 'number') {
    return null;
  }
  return { block: Math.floor(value.block), ratio: Math.min(1, Math.max(0, value.ratio)), atEnd: value.atEnd === true };
}

/**
 * Restores the anchor once the note content has rendered, and keeps it in place for a short while as
 * images etc. finish loading — until the user scrolls themselves (wheel, touch, keys, dragging the
 * scrollbar). Returns a function that stops it early.
 */
export function restoreNoteScrollAnchor(
  getScroller: () => HTMLElement | null,
  anchor: NoteScrollAnchor,
  options: { timeoutMs?: number; settleMs?: number } = {},
): () => void {
  const timeoutMs = options.timeoutMs ?? 5000;
  const settleMs = options.settleMs ?? 1500;
  const startedAt = Date.now();
  let appliedAt: number | null = null;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let listenedOn: HTMLElement | null = null;

  // Any of these means the user is scrolling/working themselves (mousedown includes the scrollbar).
  const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
  const onUserScroll = () => stop();

  const stop = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    if (listenedOn) {
      for (const type of userEvents) listenedOn.removeEventListener(type, onUserScroll);
    }
  };

  const tick = () => {
    if (stopped) return;
    const now = Date.now();
    const scroller = getScroller();
    if (scroller && !listenedOn) {
      listenedOn = scroller;
      for (const type of userEvents) scroller.addEventListener(type, onUserScroll, { passive: true });
    }
    if (scroller && applyNoteScrollAnchor(scroller, anchor)) {
      appliedAt ??= now;
    }
    const done = appliedAt !== null ? now - appliedAt >= settleMs : now - startedAt >= timeoutMs;
    if (done) return stop();
    timer = setTimeout(tick, appliedAt === null ? 50 : 150);
  };

  timer = setTimeout(tick, 0);
  return stop;
}
